import { useCallback, useMemo, useState } from 'react';
import type { FilterState, FilterValue } from '../components/ui/tableTemplate/TableTemplateTypes';
import { useDebounce } from './useDebounce';

const DEBOUNCE_DELAY_MS = 300;

export interface UseFilterStateReturn<T> {
  /** Immediate filter state — use this for UI feedback (active indicators, input values). */
  filters: FilterState<T>;
  /** Debounced filter state — use this to trigger API calls / client-side filtering. */
  debouncedFilters: FilterState<T>;
  /** Set of column keys that currently have an active filter. */
  activeFilterKeys: Set<keyof T>;
  setFilter: (key: keyof T, value: FilterValue) => void;
  clearFilter: (key: keyof T) => void;
  clearAll: () => void;
}

/**
 * Generic hook for managing table filter state.
 * Provides immediate state for UI responsiveness and a debounced copy for API/filtering calls.
 */
export function useFilterState<T>(): UseFilterStateReturn<T> {
  const [filters, setFilters] = useState<FilterState<T>>({});
  const debouncedFilters = useDebounce(filters, DEBOUNCE_DELAY_MS);

  const setFilter = useCallback((key: keyof T, value: FilterValue) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilter = useCallback((key: keyof T) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);

  const activeFilterKeys = useMemo(() => new Set(Object.keys(filters) as (keyof T)[]), [filters]);

  return { filters, debouncedFilters, activeFilterKeys, setFilter, clearFilter, clearAll };
}
